import {
  Button,
  Card,
  Box,
  Stack,
  Chip,
  Typography,
  alpha,
  useTheme,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ProjectPopup from './ProjectPopup';
import ProjectEditPopup from './ProjectEditPopup';

import { useDispatch, useSelector } from 'react-redux';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';

// Icons
import AddIcon from '@mui/icons-material/Add';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListIcon from '@mui/icons-material/List';
import SearchField from '../../components/SearchField';
import { ProjectTable, ProjectDashboardTable } from '../../components/Table';
import ContactFormModal from '../../components/Forms/ContactFormModal';
import ContactFormFields from '../../components/Forms/ContactFormFields';
import * as actions from '../../redux/actions';
import { DevWinBadge } from '../../components/DevPageBadge';
import api from '../../utils/functions/api';
import { accessRightSubMenu } from '../../utils/functions/accessRight';
import { validateThaiAddress } from '../../utils/functions/thaiZipCascade';
import useZipCascade from '../../hooks/useZipCascade';
import { emptyValues as emptyClientValues } from '../Customer/client.constants';

const _hasCommitment = (r) =>
  r.work_status !== 'done' ||
  r.finance_status !== 'completed_po' ||
  r.legal_warranty ||
  r.legal_debt ||
  r.legal_lawsuit;

const CatBox = React.memo(({ title, color, children }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: `${color}30`,
      borderRadius: 1.5,
      p: 1,
      minWidth: 130,
      boxShadow: `inset 3px 0 0 ${color}`,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        color,
        display: 'block',
        mb: 0.4,
        pb: 0.3,
        borderBottom: `1px solid ${color}20`,
        fontSize: '0.67rem',
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
));

const CRow = React.memo(
  ({ label, count, checked, excluded, color, onChange, onExclude }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 0.5,
        '&:hover': { bgcolor: `${color}08` },
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={checked}
            onChange={onChange}
            sx={{ p: 0.25, color, '&.Mui-checked': { color } }}
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.72rem', lineHeight: 1.3 }}
            >
              {label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                color,
                minWidth: 16,
                textAlign: 'right',
              }}
            >
              {count}
            </Typography>
          </Box>
        }
        sx={{ m: 0, flex: 1, px: 0.25 }}
      />
      {onExclude && (
        <Box
          onClick={onExclude}
          sx={{
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 0.5,
            cursor: 'pointer',
            mr: 0.25,
            fontSize: '0.7rem',
            fontWeight: 700,
            lineHeight: 1,
            userSelect: 'none',
            color: excluded ? '#fff' : '#94a3b8',
            bgcolor: excluded ? '#b91c1c' : 'transparent',
            '&:hover': {
              bgcolor: excluded ? '#991b1b' : '#fee2e2',
              color: excluded ? '#fff' : '#b91c1c',
            },
          }}
        >
          ✕
        </Box>
      )}
    </Box>
  ),
);

const DEFAULT_EXCLUDE_FILTER = new Set([
  'legal_warranty',
  'legal_debt',
  'legal_lawsuit',
]);

const PROJ_CACHE_KEY = 'proj_dash_cache';
const PROJ_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 ชม.

const STATUS_SUMMARY = [
  {
    key: 'has_commitment',
    label: 'มีภาระผูกพัน',
    color: '#d97706',
    bg: '#fef3c7',
    isMain: true,
    match: (r) => _hasCommitment(r),
  },
  {
    key: 'no_commitment',
    label: 'ไม่มีภาระผูกพัน',
    color: '#059669',
    bg: '#dcfce7',
    isMain: true,
    match: (r) => !_hasCommitment(r),
  },
  {
    key: 'work_waiting',
    label: 'รอเข้าดำเนินการ',
    color: '#64748b',
    bg: '#f1f5f9',
    match: (r) => !r.work_status || r.work_status === 'waiting',
  },
  {
    key: 'work_in_progress',
    label: 'ดำเนินการ',
    color: '#2563eb',
    bg: '#eff6ff',
    match: (r) => r.work_status === 'in_progress',
  },
  {
    key: 'work_done',
    label: 'เสร็จ 100%',
    color: '#059669',
    bg: '#dcfce7',
    match: (r) => r.work_status === 'done',
  },
  {
    key: 'finance_none',
    label: 'ยังไม่เคยเบิก',
    color: '#94a3b8',
    bg: '#f8fafc',
    match: (r) => !r.finance_status || r.finance_status === 'none',
  },
  {
    key: 'finance_partial',
    label: 'เบิกบางส่วน',
    color: '#f59e0b',
    bg: '#fffbeb',
    match: (r) => r.finance_status === 'partial',
  },
  {
    key: 'finance_billed_waiting',
    label: 'วางบิลรอรับ',
    color: '#d97706',
    bg: '#fff7ed',
    match: (r) => r.finance_status === 'billed_waiting',
  },
  {
    key: 'finance_last_installment',
    label: 'รอรับงวดสุดท้าย',
    color: '#c2410c',
    bg: '#fff1f2',
    match: (r) => r.finance_status === 'last_installment',
  },
  {
    key: 'finance_completed_po',
    label: 'เบิกครบ PO',
    color: '#059669',
    bg: '#dcfce7',
    match: (r) => r.finance_status === 'completed_po',
  },
  {
    key: 'bg_no_bg_usage',
    label: 'ไม่ใช้ BG แล้ว',
    color: '#374151',
    bg: '#f3f4f6',
    match: (r) => r.bg_usage_category === 'no_bg_usage',
  },
  {
    key: 'legal_warranty',
    label: 'Warranty',
    color: '#7c3aed',
    bg: '#f5f3ff',
    match: (r) => Boolean(r.legal_warranty),
  },
  {
    key: 'legal_debt',
    label: 'เจ้าหนี้',
    color: '#dc2626',
    bg: '#fef2f2',
    match: (r) => Boolean(r.legal_debt),
  },
  {
    key: 'legal_lawsuit',
    label: 'ฟ้องร้อง',
    color: '#991b1b',
    bg: '#fef2f2',
    match: (r) => Boolean(r.legal_lawsuit),
  },
];

export default function ProjectList({ title, subtitle }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.project);
  const me = useSelector((state) => state.me);
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState(0); // 0=dashboard, 1=รายการ
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const dbSearchedRef = useRef(''); // เก็บ term ที่ query DB ล่าสุด (สำหรับ scopeLabel)
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addClientSubmitError, setAddClientSubmitError] = useState('');
  const {
    handleSubmit: handleAddClientSubmit,
    control: addClientControl,
    reset: addClientReset,
    setValue: addClientSetValue,
    watch: addClientWatch,
  } = useForm({ defaultValues: emptyClientValues });
  const {
    cascade: addClientCascade,
    setZipSelections: setAddClientZipSelections,
  } = useZipCascade(addClientWatch, addClientSetValue);

  const [statusFilter, setStatusFilter] = useState('has_commitment');
  const [excludeFilter, setExcludeFilter] = useState(
    new Set(DEFAULT_EXCLUDE_FILTER),
  );
  const [jsFilter, setJsFilter] = useState(true);
  const [customerFilter, setCustomerFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [loadedScope, setLoadedScope] = useState('default'); // 'default'=JS_+has_commitment | 'all' | 'no_commitment'
  const [showPoDetail, setShowPoDetail] = useState(true);
  const [normalView, setNormalView] = useState(false);
  const [clearSnapshot, setClearSnapshot] = useState(null); // null = ปกติ, object = กำลัง cleared

  const [cacheInfo, setCacheInfo] = useState(null); // { timestamp } เมื่อโหลดจาก cache
  const unblockRef = useRef(null);
  const localDataRef = useRef({}); // mirror of localData from ProjectDashboardTable for cache merge

  const pageRef = useRef();
  const sidebarWRef = useRef(null);
  const defaultDashRowsRef = useRef([]); // cache ของ default scope — ใช้ restore ตอนกด Esc
  const [pageScale, setPageScale] = useState(1);

  // reset zoom on unmount
  useEffect(() => {
    return () => {
      const root = document.getElementById('root');
      if (root) {
        root.style.zoom = '';
        root.style.width = '';
      }
      if (pageRef.current) {
        pageRef.current.style.zoom = '';
        pageRef.current.style.width = '';
      }
    };
  }, []);

  useEffect(() => {
    // capture sidebar width once (before any zoom is applied)
    if (sidebarWRef.current === null && pageRef.current) {
      sidebarWRef.current = Math.round(
        pageRef.current.getBoundingClientRect().left,
      );
    }
    const COL_W = {
      1: 68,
      2: 76,
      3: 44,
      4: 150,
      5: 130,
      6: 78,
      7: 80,
      8: 78,
      9: 68,
      10: 72,
      11: 70,
      12: 72,
      13: 60,
      14: 80,
      15: 70,
      16: 66,
      17: 74,
      19: 88,
      20: 60,
      26: 110,
    };
    const tableW = showPoDetail
      ? 38 +
        COL_W[1] +
        COL_W[2] +
        COL_W[3] +
        COL_W[4] +
        COL_W[5] +
        COL_W[6] +
        COL_W[7] +
        COL_W[8] +
        COL_W[9] +
        COL_W[10] +
        COL_W[11] +
        COL_W[12] +
        COL_W[13] +
        COL_W[14] +
        COL_W[15] +
        COL_W[16] +
        COL_W[17] +
        COL_W[19] +
        COL_W[20] +
        COL_W[26]
      : Math.max(
          1500,
          38 +
            COL_W[1] +
            COL_W[2] +
            COL_W[3] +
            COL_W[4] +
            COL_W[5] +
            COL_W[6] +
            COL_W[10] +
            COL_W[11] +
            COL_W[12] +
            COL_W[13] +
            COL_W[14] +
            COL_W[15] +
            COL_W[16] +
            COL_W[17] +
            COL_W[19] +
            COL_W[20] +
            COL_W[26],
        );
    const naturalW = tableW + 20; // +20: reserve for TableContainer vertical scrollbar
    const calc = () => {
      const el = pageRef.current;
      if (!el) return;
      if (normalView) {
        setPageScale(1);
        el.style.zoom = '';
        el.style.width = '';
        return;
      }
      const sidebarW = sidebarWRef.current ?? 0;
      const vw = document.documentElement.clientWidth;
      const availW = vw - sidebarW;
      const scale = Math.min(1, availW / naturalW);
      setPageScale(scale);
      // zoom เฉพาะ content area — sidebar ไม่ถูกย่อ
      el.style.zoom = String(scale);
      el.style.width = scale < 1 ? `${naturalW}px` : '';
    };
    const raf = requestAnimationFrame(calc);
    window.addEventListener('resize', calc);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', calc);
      if (pageRef.current) {
        pageRef.current.style.zoom = '';
        pageRef.current.style.width = '';
      }
    };
  }, [showPoDetail, normalView]);

  const [dashRows, setDashRows] = useState([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashTotals, setDashTotals] = useState(null); // รับจาก ProjectDashboardTable
  const patchDashRow = useCallback((id, fields) => {
    setDashRows((prev) => {
      const next = prev.map((r) => (r._id === id ? { ...r, ...fields } : r));
      try {
        const raw = localStorage.getItem(PROJ_CACHE_KEY);
        if (raw) {
          const cached = JSON.parse(raw);
          cached.rows = next;
          cached.timestamp = Date.now();
          localStorage.setItem(PROJ_CACHE_KEY, JSON.stringify(cached));
        }
      } catch {}
      return next;
    });
  }, []);

  const statusCountMap = useMemo(() => {
    const counts = { _js: 0 };
    for (const r of dashRows) {
      if (/^JS_/i.test(r.project_number)) counts._js++;
      for (const { key, match } of STATUS_SUMMARY) {
        if (match(r)) counts[key] = (counts[key] || 0) + 1;
      }
    }
    return counts;
  }, [dashRows]);

  const loadAllDash = useCallback(
    async ({ jsOnly = false, sf = '', name = '', scope = null } = {}) => {
      setDashLoading(true);
      try {
        const params = new URLSearchParams({
          size: 300,
          page: 1,
          skip_ts: '1',
        });
        if (jsOnly) params.set('js_only', '1');
        // col9/col7 computed from aggregation — use has_commitment as server base, filter client-side
        const serverSf = ['col9_positive', 'col7_positive'].includes(sf)
          ? 'has_commitment'
          : sf;
        if (serverSf) params.set('status_filter', serverSf);
        if (name) params.set('name', name);
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/project?${params}`,
        );
        const rows = res.data?.rows || [];
        if (scope === 'all') defaultDashRowsRef.current = rows;
        setDashRows(rows);
        if (scope) setLoadedScope(scope);
      } catch (e) {
        console.error('loadAllDash error', e);
      } finally {
        setDashLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    let fromCache = false;
    try {
      const raw = localStorage.getItem(PROJ_CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (
          Date.now() - cached.timestamp < PROJ_CACHE_TTL &&
          cached.rows?.length > 0
        ) {
          if (cached.scope === 'all') defaultDashRowsRef.current = cached.rows;
          dbSearchedRef.current = cached.dbSearchedTerm || '';
          setDashRows(cached.rows);
          setLoadedScope(cached.scope || 'all');
          setCacheInfo({ timestamp: cached.timestamp });
          fromCache = true;
        }
      }
    } catch {}
    if (!fromCache) loadAllDash({ jsOnly: false, sf: '', scope: 'all' });
  }, [loadAllDash]);

  const handleSearchEsc = useCallback(() => {
    setSearchTerm('');
    setJsFilter(true);
    setStatusFilter('has_commitment');
    setExcludeFilter(new Set(DEFAULT_EXCLUDE_FILTER));
    setCustomerFilter('');
    setProjectFilter('');
    setClearSnapshot(null);
    if (loadedScope !== 'all') {
      setDashRows(defaultDashRowsRef.current);
      setLoadedScope('all');
    }
  }, [loadedScope]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (selectedId || createOpen) return; // ให้ dialog จัดการ Esc เอง
      handleSearchEsc();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleSearchEsc, selectedId, createOpen]);

  const handleDbSearch = useCallback(() => {
    const q = searchTerm.trim();
    if (!q) return;
    dbSearchedRef.current = q;
    setJsFilter(false);
    setStatusFilter('');
    setExcludeFilter(new Set()); // ล้าง exclude ทั้งหมดก่อนค้นหา
    setCustomerFilter('');
    setProjectFilter('');
    setClearSnapshot(null);
    loadAllDash({ name: q, jsOnly: false, sf: '', scope: 'search' });
  }, [searchTerm, loadAllDash]);

  // ── Auto-save cache on navigate away ──────────────────────────────────────────
  useEffect(() => {
    unblockRef.current = history.block((location) => {
      // save แล้ว navigate ต่อทันที ไม่ถาม
      try {
        const ld = localDataRef.current;
        const mergedRows = dashRows.map((r) => ({
          ...r,
          ...(ld[r._id] || {}),
        }));
        localStorage.setItem(
          PROJ_CACHE_KEY,
          JSON.stringify({
            rows: mergedRows,
            scope: loadedScope,
            dbSearchedTerm: dbSearchedRef.current,
            timestamp: Date.now(),
          }),
        );
      } catch {}
      unblockRef.current?.();
      unblockRef.current = null;
      history.push(
        typeof location === 'string'
          ? location
          : location.pathname + (location.search || ''),
      );
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [history, dashRows, loadedScope]);

  const isAdmin = accessRightSubMenu(me, 'PROJECT', '/project/project');

  useEffect(() => {
    dispatch(actions.projectAll({ page, size }));
    return () => {};
  }, [page, size]);

  const isFirstSearch = useRef(true);
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      dispatch(
        actions.projectAll({ page: 1, size, name: searchTerm || undefined }),
      );
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleOnclickDetail = (id) => {
    history.push(`/project/project/detail/${id}`);
  };

  const handleOpenProject = (id) => {
    const row =
      dashRows.find((r) => r._id === id || r.id === id) ||
      projects?.rows?.find((r) => r._id === id || r.id === id) ||
      null;
    setSelectedRow(row);
    setSelectedId(id);
  };
  const handleCloseProject = () => {
    setSelectedId(null);
    setSelectedRow(null);
  };

  const handleSavedProject = (isEdit = false) => {
    loadAllDash({ jsOnly: false, sf: '', scope: 'all' });
    if (!isEdit) {
      dispatch(
        actions.projectAll({ page, size, name: searchTerm || undefined }),
      );
    }
  };

  const handleAddClientOpen = () => {
    setAddClientZipSelections({ province: null, district: null });
    setAddClientSubmitError('');
    setAddClientOpen(true);
  };
  const handleAddClientClose = () => {
    setAddClientOpen(false);
    addClientReset(emptyClientValues);
  };

  const onAddClientSubmit = async (data) => {
    const zipError = validateThaiAddress(data);
    if (zipError) {
      setAddClientSubmitError(zipError);
      return;
    }
    try {
      await dispatch(actions.contactCreate({ ...data, role: 'client' }));
      handleAddClientClose();
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const renderActions = () => (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <SearchField
          label="ค้นหาโครงการ"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="ค้นหาตามชื่อหรือรหัสโครงการ..."
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
            width: 260,
            flexShrink: 0,
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: 1.5,
            height: 36,
            whiteSpace: 'nowrap',
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          เพิ่มโครงการใหม่
        </Button>
      </Stack>
    </Box>
  );

  const buildStatusChips = () => {
    const toggleStatus = (key) => {
      setStatusFilter((prev) => (prev === key ? '' : key));
      setJsFilter(false);
      setExcludeFilter((prev) => {
        const s = new Set(prev);
        s.delete(key);
        return s;
      });
    };

    const toggleExclude = (key) => {
      setExcludeFilter((prev) => {
        const s = new Set(prev);
        if (s.has(key)) s.delete(key);
        else s.add(key);
        return s;
      });
      setStatusFilter((prev) => (prev === key ? '' : prev));
    };

    const handleClearToggle = () => {
      if (clearSnapshot) {
        setStatusFilter(clearSnapshot.statusFilter);
        setJsFilter(clearSnapshot.jsFilter);
        setExcludeFilter(clearSnapshot.excludeFilter);
        setClearSnapshot(null);
      } else {
        setClearSnapshot({ statusFilter, jsFilter, excludeFilter });
        setStatusFilter('');
        setJsFilter(false);
        setExcludeFilter(new Set());
      }
    };

    const scopeLabel =
      loadedScope === 'search'
        ? `ผลค้นหา DB: "${dbSearchedRef.current}" (${dashRows.length} รายการ)`
        : loadedScope === 'default'
        ? `โหลดอยู่: JS_+มีผูกพัน (${dashRows.length})`
        : loadedScope === 'no_commitment'
        ? `โหลดอยู่: ไม่มีผูกพัน (${dashRows.length})`
        : `โหลดอยู่: ทั้งหมด (${dashRows.length})`;

    const LOAD_BTN = (color) => ({
      bgcolor: 'transparent',
      color,
      border: `1.5px dashed ${color}`,
      fontWeight: 500,
      cursor: 'pointer',
      fontSize: '0.72rem',
      '&:hover': { bgcolor: `${color}15` },
    });

    const CATS = [
      {
        title: 'ภาระผูกพัน',
        color: '#d97706',
        items: STATUS_SUMMARY.filter((s) => s.isMain),
      },
      {
        title: 'สถานะงาน',
        color: '#2563eb',
        items: STATUS_SUMMARY.filter((s) =>
          ['work_waiting', 'work_in_progress', 'work_done'].includes(s.key),
        ),
      },
      {
        title: 'การเบิกจ่าย',
        color: '#f59e0b',
        items: STATUS_SUMMARY.filter(
          (s) => s.key.startsWith('finance_') || s.key === 'bg_no_bg_usage',
        ),
      },
      {
        title: 'กฎหมาย/ความเสี่ยง',
        color: '#7c3aed',
        items: STATUS_SUMMARY.filter((s) => s.key.startsWith('legal_')),
      },
    ];

    return (
      <Box>
        {/* ── Scope row ── */}
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{ mb: 1.25, flexWrap: 'wrap' }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            {scopeLabel}
          </Typography>
          {loadedScope === 'all' && (
            <Chip
              size="small"
              label="🔄 รีเฟรช"
              onClick={() =>
                loadAllDash({ jsOnly: false, sf: '', scope: 'all' })
              }
              sx={LOAD_BTN('#6366f1')}
            />
          )}
          {loadedScope !== 'all' && (
            <Chip
              size="small"
              label="↺ กลับทั้งหมด"
              onClick={() => {
                setStatusFilter('has_commitment');
                setJsFilter(true);
                setDashRows(defaultDashRowsRef.current);
                setLoadedScope('all');
              }}
              sx={LOAD_BTN('#1e3a8a')}
            />
          )}
          {cacheInfo && (
            <Chip
              size="small"
              label={`💾 แคช ${dayjs(cacheInfo.timestamp).format(
                'DD/MM HH:mm',
              )}`}
              onDelete={() => {
                localStorage.removeItem(PROJ_CACHE_KEY);
                setCacheInfo(null);
                loadAllDash({ jsOnly: false, sf: '', scope: 'all' });
              }}
              sx={{
                fontSize: '0.67rem',
                height: 20,
                bgcolor: '#f0fdf4',
                color: '#15803d',
                border: '1px solid #86efac',
                '& .MuiChip-deleteIcon': { fontSize: 14, color: '#15803d' },
              }}
            />
          )}
          <Box sx={{ ml: 'auto' }}>
            <Chip
              size="small"
              label={clearSnapshot ? '↩ คืนค่า' : 'ล้างทั้งหมด'}
              onClick={handleClearToggle}
              sx={
                clearSnapshot
                  ? {
                      ...LOAD_BTN('#d97706'),
                      borderStyle: 'solid',
                      fontWeight: 700,
                    }
                  : LOAD_BTN('#94a3b8')
              }
            />
          </Box>
        </Stack>

        {/* ── Checkbox categories ── */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          flexWrap="wrap"
          useFlexGap
        >
          {/* JS_ */}
          <CatBox title="ประเภท" color="#b45309">
            <CRow
              label="JS_ เท่านั้น"
              count={statusCountMap._js || 0}
              checked={jsFilter}
              color="#f59e0b"
              onChange={() => setJsFilter((v) => !v)}
            />
          </CatBox>

          {CATS.map(({ title, color, items }) => (
            <CatBox key={title} title={title} color={color}>
              {items.map(({ key, label }) => (
                <CRow
                  key={key}
                  label={label}
                  count={statusCountMap[key] || 0}
                  checked={statusFilter === key}
                  excluded={excludeFilter.has(key)}
                  color={color}
                  onChange={() => toggleStatus(key)}
                  onExclude={() => toggleExclude(key)}
                />
              ))}
            </CatBox>
          ))}
        </Stack>
      </Box>
    );
  };

  const fmtMoney = (v) =>
    Number(v || 0).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const excludeCounts = useMemo(() => {
    if (excludeFilter.size === 0) return {};
    let base = jsFilter
      ? dashRows.filter((r) => /^JS_/i.test(r.project_number))
      : dashRows;
    if (statusFilter) {
      const inc = STATUS_SUMMARY.find((s) => s.key === statusFilter);
      if (inc) base = base.filter(inc.match);
    }
    const counts = {};
    for (const key of excludeFilter) {
      const s = STATUS_SUMMARY.find((item) => item.key === key);
      if (s) counts[key] = base.filter(s.match).length;
    }
    return counts;
  }, [excludeFilter, dashRows, jsFilter, statusFilter]);

  const filteredDashRows = useMemo(() => {
    let rows = dashRows;
    if (jsFilter) {
      rows = rows.filter((r) => /^JS_/i.test(r.project_number));
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.project_number || '').toLowerCase().includes(q) ||
          (r.name || '').toLowerCase().includes(q) ||
          (r.client_id?.name || '').toLowerCase().includes(q),
      );
    }
    if (customerFilter) {
      const q = customerFilter.toLowerCase();
      rows = rows.filter((r) =>
        (r.client_id?.name || r.customer || '').toLowerCase().includes(q),
      );
    }
    if (projectFilter) {
      rows = rows.filter((r) => r.project_number === projectFilter);
    }
    if (statusFilter) {
      rows = rows.filter((r) => {
        const ws = r.work_status || '';
        const fs = r.finance_status || '';
        const lw = Boolean(r.legal_warranty);
        const ld = Boolean(r.legal_debt);
        const ll = Boolean(r.legal_lawsuit);
        const hasCom = ws !== 'done' || fs !== 'completed_po' || lw || ld || ll;
        if (statusFilter === 'has_commitment') return hasCom;
        if (statusFilter === 'no_commitment') return !hasCom;
        if (statusFilter === 'work_waiting') return !ws || ws === 'waiting';
        if (statusFilter === 'work_in_progress') return ws === 'in_progress';
        if (statusFilter === 'work_done') return ws === 'done';
        if (statusFilter === 'finance_none') return !fs || fs === 'none';
        if (statusFilter === 'finance_partial') return fs === 'partial';
        if (statusFilter === 'finance_billed_waiting')
          return fs === 'billed_waiting';
        if (statusFilter === 'finance_last_installment')
          return fs === 'last_installment';
        if (statusFilter === 'finance_completed_po')
          return fs === 'completed_po';
        if (statusFilter === 'bg_no_bg_usage')
          return (r.bg_usage_category || '') === 'no_bg_usage';
        if (statusFilter === 'legal_warranty') return lw;
        if (statusFilter === 'legal_debt') return ld;
        if (statusFilter === 'legal_lawsuit') return ll;
        if (statusFilter === 'col9_positive') {
          const po = r.include_vat ? (r.cost || 0) - (r.vat || 0) : r.cost || 0;
          const col9 =
            po -
            (r.received || 0) -
            (r.seed_received || 0) -
            (r.billed_waiting || 0) -
            (r.seed_billed_waiting || 0);
          return Math.round(col9 * 100) > 0;
        }
        if (statusFilter === 'col7_positive') {
          return (
            Math.round(
              ((r.billed_waiting || 0) + (r.seed_billed_waiting || 0)) * 100,
            ) > 0
          );
        }
        return true;
      });
    }
    if (excludeFilter.size > 0) {
      const excludeKeys = [...excludeFilter];
      rows = rows.filter(
        (r) =>
          !excludeKeys.some((key) => {
            const s = STATUS_SUMMARY.find((item) => item.key === key);
            if (s) return s.match(r);
            if (key === 'col9_positive') {
              const po = r.include_vat
                ? (r.cost || 0) - (r.vat || 0)
                : r.cost || 0;
              const col9 =
                po -
                (r.received || 0) -
                (r.seed_received || 0) -
                (r.billed_waiting || 0) -
                (r.seed_billed_waiting || 0);
              return Math.round(col9 * 100) > 0;
            }
            if (key === 'col7_positive')
              return (
                Math.round(
                  ((r.billed_waiting || 0) + (r.seed_billed_waiting || 0)) *
                    100,
                ) > 0
              );
            return false;
          }),
      );
    }
    return rows;
  }, [
    dashRows,
    searchTerm,
    statusFilter,
    jsFilter,
    excludeFilter,
    customerFilter,
    projectFilter,
  ]);

  const renderSummaryCards = () => {
    // ใช้ตัวเลขจาก ProjectDashboardTable โดยตรง เพื่อให้ตรงกันเสมอ
    const card1 = dashTotals?.poRemain ?? 0; // col9
    const card2 = dashTotals?.col17 ?? 0; // col17
    const card3 = card1 - card2;
    const card4 = dashTotals?.billed ?? 0; // col7
    const card5 = card3 + card4;

    const SCard = ({ label, value, color, col, onClick, active }) => (
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          bgcolor: color,
          color: '#fff',
          borderRadius: 2,
          px: 2,
          pt: 1.25,
          pb: col ? 2 : 1.25,
          minWidth: 170,
          textAlign: 'center',
          cursor: onClick ? 'pointer' : 'default',
          outline: active ? '3px solid #fff' : 'none',
          outlineOffset: '-3px',
          boxShadow: active ? `0 0 0 3px ${color}` : 'none',
          transition: 'outline 0.1s, box-shadow 0.1s',
          '&:hover': onClick ? { filter: 'brightness(1.1)' } : {},
        }}
      >
        {col && (
          <Typography
            sx={{
              position: 'absolute',
              bottom: 4,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.6rem',
              opacity: 0.55,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {col}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{ opacity: 0.85, display: 'block', lineHeight: 1.4 }}
        >
          {label}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap' }}
        >
          {fmtMoney(value)}
        </Typography>
      </Box>
    );
    const Op = ({ children }) => (
      <Typography
        variant="h5"
        sx={{ color: 'text.secondary', alignSelf: 'center', fontWeight: 300 }}
      >
        {children}
      </Typography>
    );

    return (
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          <SCard
            label="ยอดค้างส่งงานจาก PO"
            value={card1}
            color="#2563eb"
            col="col 9"
            active={statusFilter === 'col9_positive'}
            onClick={() => {
              if (statusFilter === 'col9_positive') {
                setStatusFilter('has_commitment');
                setJsFilter(true);
              } else {
                setStatusFilter('col9_positive');
                setJsFilter(false);
                setExcludeFilter((prev) => {
                  const s = new Set(prev);
                  s.delete('col9_positive');
                  return s;
                });
              }
            }}
          />
          <Op>−</Op>
          <SCard
            label="ทุนคงเหลือ (คาดการณ์)"
            value={card2}
            color="#d97706"
            col="col 17"
          />
          <Op>=</Op>
          <SCard label="ส่วนต่าง" value={card3} color="#0891b2" />
          <Op>+</Op>
          <SCard
            label="วางบิลแล้วรอรับเงิน"
            value={card4}
            color="#059669"
            col="col 7"
            active={statusFilter === 'col7_positive'}
            onClick={() => {
              if (statusFilter === 'col7_positive') {
                setStatusFilter('has_commitment');
                setJsFilter(true);
              } else {
                setStatusFilter('col7_positive');
                setJsFilter(false);
                setExcludeFilter((prev) => {
                  const s = new Set(prev);
                  s.delete('col7_positive');
                  return s;
                });
              }
            }}
          />
          <Op>=</Op>
          <SCard label="ยอดรวม" value={card5} color="#7c3aed" />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant={normalView ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setNormalView((v) => !v)}
            sx={{
              borderRadius: 1.5,
              height: 36,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              minWidth: 0,
              fontSize: '0.72rem',
            }}
          >
            {normalView ? '🔍 Auto Zoom' : '1:1'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              borderRadius: 1.5,
              height: 36,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            เพิ่มโครงการใหม่
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAddAlt1Icon />}
            onClick={handleAddClientOpen}
            sx={{
              borderRadius: 1.5,
              height: 36,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            เพิ่มลูกค้า
          </Button>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', gap: 1, mt: 2 }}
        >
          <SearchField
            label="ค้นหาโครงการ"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleDbSearch();
            }}
            placeholder="รหัส · ชื่อโครงการ · ลูกค้า · หมายเหตุ"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.78rem',
              },
              '& .MuiInputLabel-root': { fontSize: '0.78rem' },
              '& .MuiInputBase-input::placeholder': { fontSize: '0.75rem' },
              width: 480,
              flexShrink: 0,
            }}
          />
          {searchTerm.trim() && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleDbSearch}
              disabled={dashLoading}
              sx={{
                borderRadius: 1.5,
                height: 36,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontSize: '0.72rem',
                borderColor: '#2563eb',
                color: '#2563eb',
                '&:hover': { bgcolor: '#eff6ff', borderColor: '#1d4ed8' },
              }}
            >
              {dashLoading ? 'กำลังค้นหา...' : '🔍 ค้นหาทุก record ใน DB'}
            </Button>
          )}
        </Stack>
      </Box>
    );
  };

  const renderDashboard = () => (
    <Box>
      {renderSummaryCards()}
      <Card
        sx={{
          borderRadius: 0,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <ProjectDashboardTable
          showPoDetail={showPoDetail}
          onShowPoDetailChange={setShowPoDetail}
          pageScale={pageScale}
          projects={{ rows: dashRows, total: dashRows.length }}
          isLoading={dashLoading}
          isAdmin={isAdmin}
          onClickProject={handleOpenProject}
          onContactUpdated={() =>
            loadAllDash({ jsOnly: false, sf: '', scope: 'all' })
          }
          onRowUpdated={patchDashRow}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          jsFilter={jsFilter}
          onJsFilterChange={setJsFilter}
          excludeFilter={excludeFilter}
          onExcludeFilterChange={setExcludeFilter}
          defaultExcludeFilter={DEFAULT_EXCLUDE_FILTER}
          excludeCounts={excludeCounts}
          customerFilter={customerFilter}
          onCustomerFilterChange={setCustomerFilter}
          projectFilter={projectFilter}
          onProjectFilterChange={setProjectFilter}
          statusSummaryChips={buildStatusChips()}
          onTotalsChange={setDashTotals}
          onLocalDataChange={(ld) => {
            localDataRef.current = ld;
          }}
        />
      </Card>
    </Box>
  );

  const renderList = () => (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      <ProjectTable
        projects={projects}
        handleOnclickDetail={handleOnclickDetail}
        onSelectProject={handleOpenProject}
        page={page}
        setPage={setPage}
        size={size}
        setSize={setSize}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 1.5,
          bgcolor: alpha(theme.palette.background.default, 0.6),
        }}
      >
        <Typography variant="caption" color="textSecondary">
          แสดงข้อมูลทั้งหมด {projects?.total || 0} รายการ (หน้า {page} จาก{' '}
          {Math.ceil((projects?.total || 0) / size)})
        </Typography>
        <Typography variant="caption" color="textSecondary">
          อัปเดทล่าสุด: {dayjs().format('DD/MM/YYYY HH:mm')}
        </Typography>
      </Box>
    </Card>
  );

  return (
    <div ref={pageRef}>
      {renderDashboard()}

      <DevWinBadge num={111} name="Project · Popup" open={!!selectedId} />
      <Dialog
        open={!!selectedId}
        onClose={handleCloseProject}
        maxWidth={false}
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{
          sx: {
            mt: '40px',
            maxHeight: 'calc(100vh - 56px)',
            width: 'auto',
            maxWidth: 'calc(100vw - 32px)',
          },
        }}
        hideBackdrop
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedId && (
            <ProjectPopup
              propId={selectedId}
              onClose={handleCloseProject}
              onSaved={handleSavedProject}
              initialData={selectedRow}
            />
          )}
        </DialogContent>
      </Dialog>

      <ProjectEditPopup
        open={createOpen}
        propId={null}
        title="เพิ่มโครงการใหม่"
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          dispatch(
            actions.projectAll({ page, size, name: searchTerm || undefined }),
          );
        }}
      />

      <ContactFormModal
        open={addClientOpen}
        onClose={handleAddClientClose}
        title="เพิ่มลูกค้าใหม่"
        subtitle="กรอกข้อมูลลูกค้าองค์กรหรือบุคคลธรรมดา"
        onSubmit={handleAddClientSubmit(onAddClientSubmit)}
        error={addClientSubmitError}
      >
        <ContactFormFields
          control={addClientControl}
          cascade={addClientCascade}
          setZipSelections={setAddClientZipSelections}
        />
      </ContactFormModal>
    </div>
  );
}
